import { ApplicationInsights } from "@microsoft/applicationinsights-web"
import { v4 as uuidv4 } from "uuid"

import { isProductionEnvironment } from "@/helper/app/browser-helper"

declare module "axios" {
  export interface AxiosRequestConfig {
    __appInsightsStart?: number
    __appInsightsTenant?: string
  }
}

const DEFAULT_HTTP_FLUSH_MINUTES = 1
const consoleTelemetryEnabled =
  import.meta.env.VITE_APPINSIGHTS_CONSOLE_LOGS?.trim().toLowerCase() === "true"
const telemetryEnabled = isProductionEnvironment() || consoleTelemetryEnabled

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const OBJECT_ID_SEGMENT = /^[0-9a-f]{24}$/i
const NUMERIC_ID_SEGMENT = /^\d+$/
const LONG_HEX_SEGMENT = /^[0-9a-f]{16,}$/i

interface PendingHttpDependency {
  method: string
  requestPath: string
  url: string
  durationMs: number
  status: number
  success: boolean
  target: string
  tenant: string
  requestBytes: number
  responseBytes: number
}

let appInsights: ApplicationInsights | null = null
let pendingHttpDependencies: PendingHttpDependency[] = []
let httpFlushListenersBound = false

export function getAppInsightsClient(): ApplicationInsights | null {
  return appInsights
}

export function isAppInsightsConsoleLoggingEnabled(): boolean {
  return consoleTelemetryEnabled
}

function stripQueryParams(url: string): string {
  const index = url.indexOf("?")
  return index >= 0 ? url.slice(0, index) : url
}

function buildRequestUrl(config: { url?: string; baseURL?: string }): string {
  const requestUrl = config.url ?? ""
  const fullUrl = requestUrl.startsWith("http")
    ? requestUrl
    : `${config.baseURL ?? ""}${requestUrl}`

  return stripQueryParams(fullUrl)
}

/** Collapse UUID / numeric / ObjectId path segments so endpoint cardinality stays low. */
export function normalizeEndpointPath(pathOrUrl: string): string {
  let pathname = stripQueryParams(pathOrUrl).split("#")[0] ?? ""

  try {
    pathname = new URL(pathOrUrl, "http://local.invalid").pathname
  } catch {
    // keep pathname from strip above
  }

  return pathname
    .split("/")
    .map((segment) => {
      if (!segment) return segment
      if (
        UUID_SEGMENT.test(segment) ||
        OBJECT_ID_SEGMENT.test(segment) ||
        NUMERIC_ID_SEGMENT.test(segment) ||
        LONG_HEX_SEGMENT.test(segment)
      ) {
        return "{id}"
      }
      return segment
    })
    .join("/")
}

/** Byte size for already-materialized payloads only — never JSON.stringify (can be multi‑MB). */
function knownByteLength(value: unknown): number {
  if (value == null) {
    return 0
  }

  if (typeof value === "string") {
    return new TextEncoder().encode(value).length
  }

  if (typeof ArrayBuffer !== "undefined" && value instanceof ArrayBuffer) {
    return value.byteLength
  }

  if (ArrayBuffer.isView(value)) {
    return value.byteLength
  }

  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return value.size
  }

  return 0
}

function contentLengthFromHeaders(headers: unknown): number | null {
  if (!headers || typeof headers !== "object") {
    return null
  }

  const record = headers as Record<string, unknown>
  const raw =
    record["content-length"] ??
    record["Content-Length"] ??
    (typeof (headers as { get?: (name: string) => string }).get === "function"
      ? (headers as { get: (name: string) => string }).get("content-length")
      : undefined)

  if (raw == null) {
    return null
  }

  const parsed = Number(Array.isArray(raw) ? raw[0] : raw)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

function httpFlushIntervalMs(): number {
  const fromEnv = Number(import.meta.env.VITE_APPINSIGHTS_HTTP_FLUSH_MINUTES)
  if (Number.isFinite(fromEnv) && fromEnv > 0) {
    return fromEnv * 60 * 1000
  }

  return DEFAULT_HTTP_FLUSH_MINUTES * 60 * 1000
}

export function flushAppInsightsHttpDependencies() {
  if (pendingHttpDependencies.length === 0) {
    return
  }

  const samples = pendingHttpDependencies
  pendingHttpDependencies = []

  if (consoleTelemetryEnabled) {
    console.info("[app-insights][local] would send HTTP dependencies", samples)
    return
  }

  if (!appInsights) {
    return
  }

  for (const sample of samples) {
    appInsights.trackDependencyData({
      id: uuidv4(),
      name: `${sample.method} ${sample.requestPath}`,
      data: sample.url,
      duration: sample.durationMs,
      success: sample.success,
      responseCode: sample.status,
      type: "HTTP",
      target: sample.target,
      properties: {
        method: sample.method,
        status: String(sample.status),
        tenant: sample.tenant
      },
      measurements: {
        requestBytes: sample.requestBytes,
        responseBytes: sample.responseBytes
      }
    })
  }
}

function bindHttpFlushListeners() {
  if (httpFlushListenersBound || typeof window === "undefined") {
    return
  }

  httpFlushListenersBound = true
  setInterval(flushAppInsightsHttpDependencies, httpFlushIntervalMs())

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushAppInsightsHttpDependencies()
    }
  })

  window.addEventListener("pagehide", flushAppInsightsHttpDependencies)
  window.addEventListener("beforeunload", flushAppInsightsHttpDependencies)
}

export function initApplicationInsights(): ApplicationInsights | null {
  if (!telemetryEnabled) {
    return null
  }

  if (consoleTelemetryEnabled) {
    bindHttpFlushListeners()
    console.info("[app-insights][local] telemetry enabled; metrics are logged, not sent")
    return null
  }

  const connectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING?.trim()
  if (!connectionString || appInsights) {
    return appInsights
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      disableAjaxTracking: true,
      disableFetchTracking: true,
      samplingPercentage: 100
    }
  })
  appInsights.loadAppInsights()
  bindHttpFlushListeners()

  return appInsights
}

export function isApplicationInsightsEnabled(): boolean {
  return appInsights !== null
}

export function trackPageLoadComplete(durationMs: number, tenant: string) {
  if (!telemetryEnabled) {
    return
  }

  const roundedDurationMs = Math.round(durationMs)

  if (consoleTelemetryEnabled) {
    console.info("[app-insights][local] would send page load", {
      durationMs: roundedDurationMs,
      tenant
    })
    return
  }

  if (!appInsights) {
    return
  }

  appInsights.trackEvent(
    {
      name: "page_load_complete",
      measurements: { durationMs: roundedDurationMs }
    },
    { tenant }
  )
}

export function markHttpRequestForAppInsights(config: { __appInsightsStart?: number }) {
  if (!telemetryEnabled) {
    return
  }

  config.__appInsightsStart = performance.now()
}

export function recordAppInsightsHttpResponse(
  config: {
    method?: string
    url?: string
    baseURL?: string
    data?: unknown
    headers?: unknown
    __appInsightsStart?: number
    __appInsightsTenant?: string
  },
  status: number,
  response?: {
    headers?: unknown
  }
) {
  if (!telemetryEnabled || config.__appInsightsStart === undefined) {
    return
  }

  const durationMs = performance.now() - config.__appInsightsStart
  const url = buildRequestUrl(config)
  const method = (config.method ?? "GET").toUpperCase()
  const requestPath = normalizeEndpointPath(config.url ?? url)
  const success = status >= 200 && status < 400

  let target = ""
  try {
    target = new URL(url).host
  } catch {
    target = config.baseURL ?? "unknown"
  }

  const requestBytes = contentLengthFromHeaders(config.headers) ?? knownByteLength(config.data)
  const responseBytes = contentLengthFromHeaders(response?.headers) ?? 0

  pendingHttpDependencies.push({
    method,
    requestPath,
    url,
    durationMs: Math.round(durationMs),
    status,
    success,
    target,
    tenant: config.__appInsightsTenant ?? "unknown",
    requestBytes,
    responseBytes
  })
}
